// ✅ src/pages/vendors/VendorPerformanceHub.jsx
import React, { useState, useEffect } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import axios from "axios";
import API_BASE_URL from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { canAccess } from "../../utils/permissions";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";

const index = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [cities, setCities] = useState([]);

  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const [showVendorModal, setShowVendorModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // add | edit
  const [selectedVendor, setSelectedVendor] = useState(null);

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  // ✅ UPDATED: single pincode removed, multi pincodes added
  const [formData, setFormData] = useState({
    name: "",
    number: "",
    email: "",
    password: "",
    city: "",
    address: "",
    gender: "male",
    dob: "",
    age: "",
    radius: "",
    // ✅ MULTI PINCODES
    pincodes: [{ pincode: "", priority: 0, radiusKm: "" }],
  });

  const genders = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  /* ---------------------------
     Fetch Cities
  ---------------------------- */
  const fetchCities = async () => {
    try {
      const res = await axiosInstance.get(`${API_BASE_URL}/api/cities`);
      setCities(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_BASE_URL}/api/vendors`, {
        params: { search, status },
      });

      // backend may return { vendors: [] } or [] depending on your API
      setVendors(response.data?.vendors || response.data || []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status]);

  /* ---------------------------
     Normal Inputs
  ---------------------------- */
  const handleInputChange = (e) => {
    try {
      const { name, value, type, checked } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    } catch (err) {
      console.error("handleInputChange error:", err);
    }
  };

  /* ---------------------------
     ✅ MULTI PINCODE HELPERS
  ---------------------------- */
  const addPincodeRow = () => {
    try {
      setFormData((prev) => ({
        ...prev,
        pincodes: [
          ...(prev.pincodes || []),
          { pincode: "", priority: 0, radiusKm: "" },
        ],
      }));
    } catch (err) {
      console.error("addPincodeRow error:", err);
    }
  };

  const removePincodeRow = (idx) => {
    try {
      setFormData((prev) => {
        const next = [...(prev.pincodes || [])];
        next.splice(idx, 1);

        // keep at least one row
        return {
          ...prev,
          pincodes: next.length
            ? next
            : [{ pincode: "", priority: 0, radiusKm: "" }],
        };
      });
    } catch (err) {
      console.error("removePincodeRow error:", err);
    }
  };

  const updatePincodeField = (idx, field, value) => {
    try {
      setFormData((prev) => {
        const next = [...(prev.pincodes || [])];
        next[idx] = { ...next[idx], [field]: value };
        return { ...prev, pincodes: next };
      });
    } catch (err) {
      console.error("updatePincodeField error:", err);
    }
  };

  /* ---------------------------
     Modal Openers
  ---------------------------- */
  const resetForm = () => {
    try {
      setFormData({
        name: "",
        number: "",
        email: "",
        password: "",
        city: "",
        address: "",
        gender: "male",
        dob: "",
        age: "",
        radius: "",
        pincodes: [{ pincode: "", priority: 0, radiusKm: "" }],
      });
    } catch (err) {
      console.error("resetForm error:", err);
    }
  };

  const handleAddVendor = () => {
    try {
      setModalMode("add");
      setSelectedVendor(null);
      resetForm();
      setShowVendorModal(true);
    } catch (err) {
      console.error("handleAddVendor error:", err);
    }
  };

  const handleEdit = (vendor) => {
    try {
      setModalMode("edit");
      setSelectedVendor(vendor);

      setFormData({
        name: vendor?.name || "",
        number: vendor?.number || "",
        email: vendor?.email || "",
        password: "",
        city: vendor?.city || "",
        address: vendor?.address || "",
        gender: vendor?.gender || "male",
        dob: vendor?.dob ? new Date(vendor.dob).toISOString().split("T")[0] : "",
        age: vendor?.age ?? "",
        radius: vendor?.radius ?? "",
        // ✅ map pincodes relation
        pincodes:
          vendor?.pincodes?.length
            ? vendor.pincodes.map((p) => ({
                pincode: p?.pincode || "",
                priority: p?.priority ?? 0,
                radiusKm: p?.radiusKm ?? "",
              }))
            : [{ pincode: "", priority: 0, radiusKm: "" }],
      });

      setShowVendorModal(true);
    } catch (err) {
      console.error("handleEdit error:", err);
    }
  };

  const handleView = (vendor) => {
    try {
      navigate(`/vendors/${vendor.id}`, { state: { vendor } });
    } catch (err) {
      console.error("handleView error:", err);
    }
  };

  /* ---------------------------
     Submit (Add/Edit)
     Payload matches backend:
       pincodes: [{ pincode, priority, radiusKm }]
  ---------------------------- */
  const handleSubmitVendor = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        age: formData.age === "" ? null : Number(formData.age),
        radius: formData.radius === "" ? null : Number(formData.radius),
        pincodes: (formData.pincodes || [])
          .map((p) => ({
            pincode: String(p?.pincode || "").trim(),
            priority: p?.priority === "" ? 0 : Number(p?.priority ?? 0),
            radiusKm: p?.radiusKm === "" ? null : Number(p?.radiusKm),
          }))
          .filter((p) => p.pincode),
      };

      if (!payload.pincodes.length) {
        alert("Please add at least one valid pincode.");
        return;
      }

      if (modalMode === "add") {
        // register requires authenticateUser in your router
        await axios.post(`${API_BASE_URL}/api/vendors/register`, payload, {
          withCredentials: true,
        });
      } else {
        const updateData = { ...payload };
        if (!updateData.password) delete updateData.password;

        await axios.put(
          `${API_BASE_URL}/api/vendors/${selectedVendor.id}`,
          updateData
        );
      }

      setShowVendorModal(false);
      resetForm();
      await fetchVendors();
      setLastUpdated(new Date());
    } catch (error) {
      console.error(
        `Error ${modalMode === "add" ? "adding" : "updating"} vendor:`,
        error
      );

      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";

      alert(message);
    }
  };

  /* ---------------------------
     Delete
  ---------------------------- */
  const handleDelete = async (vendorId) => {
    try {
      if (!window.confirm("Are you sure you want to delete this vendor?")) return;

      await axios.delete(`${API_BASE_URL}/api/vendors/${vendorId}`);
      await fetchVendors();
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error deleting vendor:", error);
      alert("Failed to delete vendor");
    }
  };

  /* ---------------------------
     Block / Unblock
  ---------------------------- */
  const handleToggleBlock = async (vendorId) => {
    try {
      await axios.put(`${API_BASE_URL}/api/vendors/block/${vendorId}`);
      await fetchVendors();
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error toggling block status:", error);
      alert("Failed to update vendor status");
    }
  };

  /* ---------------------------
     Refresh
  ---------------------------- */
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchVendors();
      setLastUpdated(new Date());
      setTimeout(() => setRefreshing(false), 800);
    } catch (err) {
      console.error("handleRefresh error:", err);
      setRefreshing(false);
    }
  };

  /* ---------------------------
     Sidebar
  ---------------------------- */
  const handleSidebarToggle = () => setSidebarOpen(!sidebarOpen);
  const handleSidebarClose = () => setSidebarOpen(false);

  /* ---------------------------
     UI Helpers
  ---------------------------- */
  const getBlockBadge = (block) =>
    block
      ? "bg-red-50 text-red-700 border border-red-200"
      : "bg-green-50 text-green-700 border border-green-200";

  const getLoginStatusBadge = (vendor) =>
    vendor?.isOnline
      ? "bg-green-50 text-green-700 border border-green-200"
      : "bg-gray-50 text-gray-700 border border-gray-200";

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: "N/A", time: "" };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
    };
  };

  const breadcrumbItems = [
    { label: "Dashboard", path: "/", icon: "Home" },
    {
      label: "Vendor Management",
      path: "/vendor-performance-hub",
      icon: "Truck",
      current: true,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleSidebarToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`
          pt-16 transition-all duration-300
          ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}
        `}
      >
        <div className="p-4">
          {/* Page Header */}
          <div className="mb-4">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mt-3">
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  Vendor Performance Hub
                </h1>
                <p className="text-xs text-gray-600 mt-0.5">
                  Manage vendor relationships and track performance metrics
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-500">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </span>
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  >
                    {refreshing ? "Refreshing..." : "Refresh"}
                  </button>
                </div>
              </div>

              <Button
                onClick={handleAddVendor}
                disabled={!canAccess(currentUser, "canCreate")}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2"
              >
                <Icon name="Plus" size={14} className="mr-1" />
                Add Vendor
              </Button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm mb-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Search Vendors
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <Icon name="Search" size={14} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name, number, or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                  />
                </div>
              </div>

              <div className="sm:w-40">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Login Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                >
                  <option value="">All Status</option>
                  <option value="active">Login Active</option>
                  <option value="inactive">Login Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Vendors Table */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            {/* Table Header */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Vendor List
                  </h3>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {vendors.length} vendors found
                  </p>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-12">
                      SL No
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Vendor Name
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Earnings
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-6 text-center">
                        <div className="flex justify-center items-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Loading vendors...
                        </p>
                      </td>
                    </tr>
                  ) : vendors.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-6 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <Icon
                            name="Truck"
                            size={32}
                            className="text-gray-400 mb-2"
                          />
                          <p className="text-gray-500 font-medium text-xs">
                            No vendors found
                          </p>
                          <p className="text-gray-400 text-xs mt-0.5">
                            {search || status
                              ? "Try adjusting your search or filters"
                              : "Get started by adding your first vendor"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    vendors.map((vendor, index) => (
                      <tr
                        key={vendor.id}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        {/* SL No */}
                        <td className="px-3 py-2 text-gray-500 font-medium">
                          {index + 1}
                        </td>

                        {/* Created Date */}
                        <td className="px-3 py-2">
                          <div className="text-xs text-gray-900">
                            {formatDateTime(vendor.createdAt).date}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDateTime(vendor.createdAt).time}
                          </div>
                        </td>

                        {/* Vendor Info */}
                        <td className="px-3 py-2">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-7 w-7 bg-blue-100 rounded-md flex items-center justify-center">
                              <Icon
                                name="Building"
                                size={14}
                                className="text-blue-600"
                              />
                            </div>
                            <div className="ml-2">
                              <div className="text-xs font-medium text-gray-900">
                                {vendor.name}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Contact */}
                        <td className="px-3 py-2">
                          <div className="text-xs text-gray-900">
                            {vendor.email || "N/A"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {vendor.number || "N/A"}
                          </div>
                        </td>

                        {/* Location */}
                        <td className="px-3 py-2">
                          <div className="text-xs text-gray-900">
                            {vendor.city || "N/A"}
                          </div>

                          {/* ✅ MULTI PINCODES display */}
                          <div className="text-xs text-gray-500">
                            {vendor.pincodes?.length
                              ? vendor.pincodes.map((p) => p.pincode).join(", ")
                              : "N/A"}
                          </div>
                        </td>

                        {/* Address */}
                        <td className="px-3 py-2">
                          <div
                            className="text-xs text-gray-900 max-w-[160px] truncate"
                            title={vendor.address}
                          >
                            {vendor.address || "N/A"}
                          </div>
                        </td>

                        {/* Earnings */}
                        <td className="px-3 py-2">
                          <div className="text-xs font-medium text-gray-900">
                            ₹{Number(vendor.earnings || 0).toFixed(2)}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-2">
                          <div className="flex flex-col gap-1">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getLoginStatusBadge(
                                vendor
                              )}`}
                            >
                              {vendor.isOnline ? "Login Active" : "Login Inactive"}
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getBlockBadge(
                                vendor.block
                              )}`}
                            >
                              {vendor.block ? "Blocked" : "Active"}
                            </span>
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-3 py-2">
                          <div className="flex justify-end space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Eye"
                              className="text-green-600 hover:text-green-800 text-xs px-2 py-1"
                              onClick={() => handleView(vendor)}
                            />

                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Edit"
                              disabled={!canAccess(currentUser, "canEdit")}
                              className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1"
                              onClick={() => handleEdit(vendor)}
                            />

                            <Button
                              variant="ghost"
                              size="sm"
                              iconName={vendor.block ? "Unlock" : "Lock"}
                              disabled={!canAccess(currentUser, "canEdit")}
                              className={`text-xs px-2 py-1 ${
                                vendor.block
                                  ? "text-green-600 hover:text-green-800"
                                  : "text-orange-600 hover:text-orange-800"
                              }`}
                              onClick={() => handleToggleBlock(vendor.id)}
                            />

                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Trash2"
                              disabled={!canAccess(currentUser, "canDelete")}
                              className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                              onClick={() => handleDelete(vendor.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Vendor Modal (Add/Edit) */}
      {showVendorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                  {modalMode === "add" ? "Add New Vendor" : "Edit Vendor"}
                </h3>
                <button
                  onClick={() => setShowVendorModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitVendor}>
              <div className="px-4 py-3 space-y-3 max-h-[60vh] overflow-y-auto">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Vendor Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Vendor Number *
                    </label>
                    <input
                      type="number"
                      name="number"
                      value={formData.number}
                      onChange={handleInputChange}
                      required
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {modalMode === "add" ? "Password *" : "New Password"}
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required={modalMode === "add"}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={modalMode === "edit" ? "Leave blank to keep current" : ""}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {genders.map((gender) => (
                        <option key={gender.value} value={gender.value}>
                          {gender.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      City *
                    </label>
                    <select
                      name="city"
                      value={formData.city}
                      required
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city.id || city._id || city} value={city.name || city}>
                          {city.name || city}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* ✅ MULTI PINCODES */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-gray-700">
                      Service Pincodes *
                    </label>
                    <button
                      type="button"
                      onClick={addPincodeRow}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <Icon name="Plus" size={14} />
                      Add Pincode
                    </button>
                  </div>

                  <div className="space-y-2">
                    {(formData.pincodes || []).map((p, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end bg-white p-2 rounded-md border border-gray-200"
                      >
                        <div className="md:col-span-5">
                          <label className="block text-[11px] text-gray-600 mb-1">
                            Pincode {idx === 0 ? "*" : ""}
                          </label>
                          <input
                            type="number"
                            value={p.pincode}
                            onChange={(e) => updatePincodeField(idx, "pincode", e.target.value)}
                            required={idx === 0}
                            placeholder="e.g. 560061"
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>

                     

                        <div className="md:col-span-1 flex justify-end">
                          <button
                            type="button"
                            onClick={() => removePincodeRow(idx)}
                            disabled={(formData.pincodes || []).length === 1}
                            className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-40"
                            title="Remove"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                 
                </div>

                {/* Location + Other */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Age
                    </label>
                    <input
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Default Service Radius (km)
                    </label>
                    <input
                      type="number"
                      name="radius"
                      value={formData.radius}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowVendorModal(false)}
                  className="text-xs px-3 py-1.5"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5"
                >
                  {modalMode === "add" ? "Add Vendor" : "Update Vendor"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default index;